import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";

import { Language, LanguageCode, supportedLanguages } from "@/i18n/config";

const LocaleSelector: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const languages: Language[] = supportedLanguages;

  const handleChangeLanguage = (lang: LanguageCode) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [ref, setOpen]);

  return (
    // `navbar-tooltip navbar-tooltip-bottom` adds the hover tooltip
    // below the trigger (matches the other navbar icons). The
    // existing `relative` positioning is preserved for the language
    // dropdown's `absolute right-0` anchoring.
    <div ref={ref} className="navbar-tooltip navbar-tooltip-bottom relative">
      <label htmlFor="langIcon" className="sr-only">
        {t("general.language")}
      </label>
      <button
        id="langIcon"
        onClick={() => setOpen(!open)}
        className="text-on-primary p-2 rounded-md hover:bg-primary/80"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t("general.changeLanguage")}
      >
        <Globe size={16} />
      </button>
      {/* Tooltip — uses the same i18n key as the aria-label so the
          tooltip and screen-reader announcements stay in sync. */}
      <span className="navbar-tooltip-text" role="tooltip">
        {t("general.changeLanguage")}
      </span>

      {open && (
        <div
          className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-surface ring-1 ring-border ring-opacity-5 focus:outline-none"
          role="menu"
          aria-orientation="vertical"
        >
          <div className="py-1" role="none">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleChangeLanguage(lang.code)}
                className={`w-full text-left px-4 py-2 text-sm ${i18n.language === lang.code ? "font-semibold text-primary" : "text-text"} hover:bg-surface-alt`}
                role="menuitem"
              >
                {lang.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocaleSelector;
